// ============================================================
// FICHIER : DashboardUS.tsx
// Dashboard "US RAA" — carte interactive des hexagones H3 de mobilité
// pour les 15 focus cities, alimentée par la requête CARTO fournie :
//
//   SELECT * FROM us_hex_table_2025_05_filtered
//   WHERE toKeep = "true"
//   AND airport_city_catchment IN {{airport_city_state_iata}}
//   AND o_metropolitan_area_clean IN {{metro_areas}}
//   AND Total_weekly_trips > 1
//   AND distance_airport_h3_destination_NM >= {{gcd_nm_from}}
//   AND distance_airport_h3_destination_NM <= {{gcd_nm_to}}
//
// Voir layers/fetchLayers.us.ts pour l'implémentation exacte du WHERE.
// ============================================================

import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cellToBoundary } from "h3-js";
import { Page } from "../../App";
import {
  FOCUS_CITIES,
  DEFAULT_GCD_NM_FROM,
  DEFAULT_GCD_NM_TO,
  GCD_NM_MAX,
} from "../../config/carto.us";
import { fetchUSHexData, getH3Index, USHexRow } from "../../layers/fetchLayers.us";
import MapLoadingOverlay from "../../components/MapLoadingOverlay";
import "./DashboardUS.css";
import { cartoBasemapUrl } from "../../config/carto";

interface Props {
  navigate: (p: Page) => void;
}

// Paliers de couleur pour Total_weekly_trips (même logique que MobilityMarket India)
const THRESHOLDS = [
  { min: 0, max: 50, label: "0 – 50", color: "#003d33" },
  { min: 50, max: 150, label: "50 – 150", color: "#005c4d" },
  { min: 150, max: 400, label: "150 – 400", color: "#009A84" },
  { min: 400, max: 1000, label: "400 – 1k", color: "#33b39e" },
  { min: 1000, max: 3000, label: "1k – 3k", color: "#66ccb5" },
  { min: 3000, max: Infinity, label: "Above 3k", color: "#99e6d5" },
];

function colorFor(trips: number) {
  const t = THRESHOLDS.find((th) => trips >= th.min && trips < th.max);
  return t ? t.color : THRESHOLDS[0].color;
}

export default function DashboardUS({ navigate }: Props) {
  const mapRef = useRef<L.Map | null>(null);
  const hexLayerRef = useRef<L.LayerGroup | null>(null);
  const fetchKeyRef = useRef(0);

  // NOTE: selectedCatchments stores city names (matching the airport_city_catchment
  // column being filtered), not IATA codes — see config/carto.us.ts CATCHMENT_OPTIONS.
  const [selectedCatchments, setSelectedCatchments] = useState<string[]>(["Indianapolis"]);
  const [gcdFrom, setGcdFrom] = useState(DEFAULT_GCD_NM_FROM);
  const [gcdTo, setGcdTo] = useState(DEFAULT_GCD_NM_TO);
  const [metroFilter, setMetroFilter] = useState(""); // texte libre -> o_metropolitan_area_clean (optionnel)

  const [rows, setRows] = useState<USHexRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleCatchment = (city: string) =>
    setSelectedCatchments((prev) =>
      prev.includes(city) ? prev.filter((x) => x !== city) : [...prev, city]
    );

  // ── Init carte (centrée sur Middle America) ──
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map("us-dash-map", {
      center: [38.5, -85.5],
      zoom: 6,
      zoomControl: false,
      preferCanvas: true,
    });
    L.tileLayer(cartoBasemapUrl(),{
      attribution: "© CARTO",
      crossOrigin: true,
      noWrap: true,
    }).addTo(map);
    mapRef.current = map;
    hexLayerRef.current = L.layerGroup().addTo(map);

    // Marqueurs des 15 focus cities + hubs, toujours visibles pour l'orientation
    FOCUS_CITIES.forEach((c) => {
      L.circleMarker([c.lat, c.lng], {
        radius: 4,
        color: "#D9C700",
        fillColor: "#D9C700",
        fillOpacity: 0.9,
        weight: 1,
      })
        .bindTooltip(`${c.iata} · ${c.city}, ${c.state}`, { direction: "top", className: "us-dash-tip" })
        .addTo(map);
    });
  }, []);

  // ── Fetch + rendu des hexagones à chaque changement de filtre ──
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !hexLayerRef.current) return;

    fetchKeyRef.current += 1;
    const myKey = fetchKeyRef.current;

    hexLayerRef.current.clearLayers();
    setRows([]);

    if (selectedCatchments.length === 0) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    fetchUSHexData({
      catchments: selectedCatchments,
      metroAreas: metroFilter.trim() ? [metroFilter.trim()] : [],
      gcdNmFrom: gcdFrom,
      gcdNmTo: gcdTo,
    })
      .then((data) => {
        if (myKey !== fetchKeyRef.current) return;
        setRows(data);
        setLoading(false);

        const layer = hexLayerRef.current;
        if (!layer) return;

        data.forEach((row) => {
          const h3 = getH3Index(row);
          const trips = Number(row.total_weekly_trips) || 0;
          if (!h3) return;
          try {
            const boundary = cellToBoundary(h3); // [[lat, lng], ...]
            const poly = L.polygon(boundary as [number, number][], {
              color: colorFor(trips),
              weight: 0.5,
              fillColor: colorFor(trips),
              fillOpacity: 0.55,
            });
            poly.bindTooltip(
              `<b>${trips.toLocaleString()}</b> weekly trips<br/>${row.o_metropolitan_area_clean || ""}`,
              { sticky: true, className: "us-dash-tip" }
            );
            poly.addTo(layer);
          } catch {
            // h3 invalide / colonne inattendue — on ignore la ligne
          }
        });
      })
      .catch((e) => {
        if (myKey !== fetchKeyRef.current) return;
        setError(e.message || "Error loading data — check CARTO_TOKEN / table schema");
        setLoading(false);
      });
  }, [selectedCatchments, metroFilter, gcdFrom, gcdTo]);

  const totalTrips = rows.reduce((acc, r) => acc + (Number(r.total_weekly_trips) || 0), 0);
  const hexCount = rows.length;

  return (
    <div className="usdb">
      <div id="us-dash-map" className="usdb-map" />
      <MapLoadingOverlay loading={loading} label="MobilityMonitor data" />

      {/* ── TOP BAR (sous la barre ATR globale) ── */}
      <div className="usdb-topbar">
        <div className="usdb-topbar-title">
          <span className="usdb-topbar-dot" />
          US RAA · MobilityMonitor Dashboard
        </div>
        <div className="usdb-topbar-right">
          {loading && (
            <div className="usdb-pill usdb-pill--loading">
              <span className="usdb-pill-dot" /> Loading
            </div>
          )}
          {error && <div className="usdb-pill usdb-pill--error">{error}</div>}
          <button className="usdb-back-btn" onClick={() => navigate("usStory")}>
            ← Story
          </button>
        </div>
      </div>

      {/* ── STATS ── */}
      <div className="usdb-stats">
        <div className="usdb-stat-card">
          <div className="usdb-stat-val" style={{ color: "#009A84" }}>
            {loading ? "—" : hexCount.toLocaleString()}
          </div>
          <div className="usdb-stat-label">H3 hexagons</div>
        </div>
        <div className="usdb-stat-card">
          <div className="usdb-stat-val" style={{ color: "#56C177" }}>
            {loading ? "—" : totalTrips.toLocaleString()}
          </div>
          <div className="usdb-stat-label">Weekly trips (sum)</div>
        </div>
        <div className="usdb-stat-card">
          <div className="usdb-stat-val" style={{ color: "#D9C700" }}>
            {selectedCatchments.length}
          </div>
          <div className="usdb-stat-label">Focus cities selected</div>
        </div>
      </div>

      {/* ── SIDEBAR FILTRES ── */}
      <div className="usdb-sidebar">
        <div className="usdb-sidebar-title">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009A84" strokeWidth="2">
            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
          </svg>
          Airport catchment
        </div>
        <div className="usdb-chip-grid">
          {FOCUS_CITIES.map((c) => {
            const active = selectedCatchments.includes(c.city);
            return (
              <button
                key={c.iata}
                className={`usdb-chip ${active ? "usdb-chip--on" : ""}`}
                onClick={() => toggleCatchment(c.city)}
                title={`${c.city}, ${c.state}`}
              >
                {c.iata}
              </button>
            );
          })}
        </div>

        <div className="usdb-sidebar-title" style={{ marginTop: 24 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009A84" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20" />
          </svg>
          Metro area (optional)
        </div>
        <input
          className="usdb-input"
          placeholder="e.g. Pittsburgh, PA"
          value={metroFilter}
          onChange={(e) => setMetroFilter(e.target.value)}
        />
        <div className="usdb-hint">
          Matches <code>o_metropolitan_area_clean</code> exactly — leave blank to include all metro areas.
        </div>

        <div className="usdb-sidebar-title" style={{ marginTop: 24 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#009A84" strokeWidth="2">
            <path d="M3 3v18h18M8 17V9m4 8V5m4 12v-6" />
          </svg>
          Distance (NM)
        </div>
        <div className="usdb-range-row">
          <input
            type="number"
            className="usdb-input usdb-input--num"
            min={0}
            max={gcdTo}
            value={gcdFrom}
            onChange={(e) => setGcdFrom(Math.max(0, Math.min(Number(e.target.value), gcdTo)))}
          />
          <span>to</span>
          <input
            type="number"
            className="usdb-input usdb-input--num"
            min={gcdFrom}
            max={GCD_NM_MAX}
            value={gcdTo}
            onChange={(e) => setGcdTo(Math.max(gcdFrom, Math.min(Number(e.target.value), GCD_NM_MAX)))}
          />
        </div>
        <input
          type="range"
          min={0}
          max={GCD_NM_MAX}
          value={gcdTo}
          onChange={(e) => setGcdTo(Math.max(gcdFrom, Number(e.target.value)))}
          className="usdb-slider"
        />
        <div className="usdb-hint">
          Reproduces <code>distance_airport_h3_destination_NM</code> range · story default: 0–400NM
        </div>
      </div>

      {/* ── LEGENDE ── */}
      <div className="usdb-legend">
        <div className="usdb-legend-title">Weekly trips</div>
        {THRESHOLDS.map((t) => (
          <div key={t.label} className="usdb-legend-item">
            <span className="usdb-legend-dot" style={{ background: t.color }} />
            {t.label}
          </div>
        ))}
        <div className="usdb-legend-item" style={{ marginTop: 6 }}>
          <span className="usdb-legend-dot" style={{ background: "#D9C700" }} />
          Focus city
        </div>
      </div>
    </div>
  );
}
